<script setup>
import { onMounted, ref, watch } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, requiredIf } from "@vuelidate/validators";
import { ArgonLoadingButton } from "@/components";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonSelect from "@/components/ArgonSelect.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonSwitch from "@/components/ArgonSwitch.vue";
import TabsBootstrap from "@/components/TabsBootstrap.vue";
import { accountStore } from "@/_store/accountStore";
import { errorUtils } from "@/utils";
import { PLAN_TYPE_OPTIONS } from "@/utils/formatters";
import {
  DEFAULT_MAP_STYLE,
  MAP_STYLE_OPTIONS,
} from "@/views/maps/map-style-utils";

const store = accountStore();
const loading = ref(false);
const fetching = ref(false);
const snackbar = ref(null);
const currentTab = ref("beepacket");

const settingsTabs = [
  { label: "Beepacket", value: "beepacket" },
  { label: "Mapas", value: "maps" },
  { label: "Planes", value: "plans" },
  { label: "App móvil", value: "mobile_app" },
  { label: "Transportista", value: "transportista" },
];

const localeOptions = [
  { value: "es-ES", label: "España (Castellano)" },
  { value: "ca-ES", label: "Cataluña (Catalán)" },
  { value: "eu-ES", label: "País Vasco (Euskera)" },
  { value: "gl-ES", label: "Galicia (Gallego)" },
  { value: "ast-ES", label: "Asturias (Asturiano)" },
  { value: "an-ES", label: "Aragón (Aragonés)" }
];

const planViewOptions = [
  { value: "table", label: "Lista", icon: "fa fa-list" },
  { value: "calendar", label: "Calendario", icon: "fa fa-calendar" },
];

const mobileFinalizeOptions = [
  { value: "NO_PEDIR_NADA", label: "No pedir nada" },
  { value: "PEDIR_FOTO", label: "Pedir foto" },
  { value: "PEDIR_DNI", label: "Pedir DNI" },
  { value: "PEDIR_FOTO_O_DNI", label: "Pedir foto o DNI" },
];

const settings = ref({
  beepacket_enabled: false,
  beepacket_account_id: "",
  maps_locale: "",
  map_default_style: DEFAULT_MAP_STYLE,
  plans_default_view: "table",
  default_plan_type: "RECOGIDA",
  pickup_request_signature: false,
  delivery_request_signature: false,
  pickup_finalize: "NO_PEDIR_NADA",
  delivery_finalize: "NO_PEDIR_NADA",
  name: "",
  vat_number: "",
  address: "",
});

const beepacketAccountRequired = requiredIf(
  () => settings.value.beepacket_enabled
);

const rules = {
  beepacket_account_id: {
    required: helpers.withMessage(
      "La cuenta de beepaket es obligatoria",
      beepacketAccountRequired
    ),
  },
  name: {
    required: helpers.withMessage("La razón social es obligatoria", required),
  },
  vat_number: {
    required: helpers.withMessage("El NIF es obligatorio", required),
  },
  address: {
    required: helpers.withMessage("La dirección es obligatoria", required),
  },
};

const v$ = ref(useVuelidate(rules, settings));

function closeSnackbar() {
  snackbar.value = null;
}

function resetValidation() {
  v$.value.$reset();
}

function fillForm(account) {
  settings.value = {
    beepacket_enabled: !!account?.beepacket_enabled,
    beepacket_account_id: account?.beepacket_account_id || "",
    maps_locale: account?.preferences?.maps?.locale || "",
    map_default_style:
      account?.preferences?.maps?.default_style || DEFAULT_MAP_STYLE,
    plans_default_view:
      account?.preferences?.plans?.default_view || "table",
    default_plan_type:
      account?.preferences?.plans?.default_plan_type || "RECOGIDA",
    pickup_request_signature:
      !!account?.preferences?.mobile_app?.pickup_request_signature,
    delivery_request_signature:
      !!account?.preferences?.mobile_app?.delivery_request_signature,
    pickup_finalize:
      account?.preferences?.mobile_app?.pickup_finalize || "NO_PEDIR_NADA",
    delivery_finalize:
      account?.preferences?.mobile_app?.delivery_finalize || "NO_PEDIR_NADA",
    name: account?.name || "",
    vat_number: account?.vat_number || "",
    address: account?.address || "",
  };
  resetValidation();
}

function buildPayload() {
  return {
    beepacket_enabled: !!settings.value.beepacket_enabled,
    beepacket_account_id: settings.value.beepacket_enabled
      ? String(settings.value.beepacket_account_id || "").trim()
      : "",
    name: settings.value.name.trim(),
    vat_number: settings.value.vat_number.trim(),
    address: settings.value.address.trim(),
    preferences: {
      ...(store.account?.preferences || {}),
      maps: {
        ...(store.account?.preferences?.maps || {}),
        locale: settings.value.maps_locale,
        default_style: settings.value.map_default_style,
      },
      plans: {
        ...(store.account?.preferences?.plans || {}),
        default_view: settings.value.plans_default_view,
        default_plan_type: settings.value.default_plan_type,
      },
      mobile_app: {
        ...(store.account?.preferences?.mobile_app || {}),
        pickup_request_signature: !!settings.value.pickup_request_signature,
        delivery_request_signature: !!settings.value.delivery_request_signature,
        pickup_finalize: settings.value.pickup_finalize,
        delivery_finalize: settings.value.delivery_finalize,
      },
    },
  };
}

async function validate() {
  await v$.value.$validate();
  return v$.value.$errors;
}

function save() {
  validate().then((values) => {
    let message = "";

    for (const value of values) {
      message = `${message}<li>${value.$message}</li>`;
    }

    if (message !== "") {
      snackbar.value = {
        title: "Error",
        message: `<ul>${message}</ul>`,
        type: "danger",
        color: "white",
      };
      return;
    }

    loading.value = true;
    store.clearAccountState();
    store.updateAccount(buildPayload()).catch(() => null);
  });
}

onMounted(() => {
  fetching.value = true;
  store.clearAccountState();
  store
    .getAccount()
    .then((result) => {
      fillForm(result);
    })
    .catch(() => null)
    .finally(() => {
      fetching.value = false;
    });
});

watch(
  () => settings.value.beepacket_enabled,
  (enabled) => {
    if (!enabled) {
      settings.value.beepacket_account_id = "";
    }
  }
);

watch(
  () => store.account,
  (value) => {
    if (value) {
      fillForm(value);
    }
  }
);

watch(
  () => store.error,
  (value) => {
    if (value) {
      snackbar.value = {
        title: "Error",
        message: errorUtils.getErrorMessageHtml(value),
        type: "danger",
        color: "white",
      };
      loading.value = false;
    }
  }
);

watch(
  () => store.accountUpdated,
  (value) => {
    if (value) {
      fillForm(value);
      snackbar.value = {
        title: "Guardado",
        message: "Ajustes de cuenta guardados.",
        type: "info",
        color: "white",
      };
      loading.value = false;
    }
  }
);
</script>

<template>
  <div class="card h-100">
    <div class="card-header pb-0">
      <p class="mb-0">Cuenta</p>
    </div>
    <div class="card-body">
      <tabs-bootstrap
        v-model="currentTab"
        :tabs="settingsTabs"
        size="sm"
        refined
        style-nav="nav-underline"
      />

      <form role="form">
        <div v-show="currentTab === 'beepacket'" class="tab-pane-section">
          <div class="row">
            <div class="col-12">
              <argon-switch
                id="beepacket-enabled"
                v-model="settings.beepacket_enabled"
                name="beepacket-enabled"
              >
                Conéctate a Beepaket
              </argon-switch>
            </div>
          </div>

          <div v-if="settings.beepacket_enabled" class="row">
            <div class="col-details">
              <label for="beepacket-account-id" class="col-form-label"></label>
            </div>
            <div class="col">
              <argon-input
                id="beepacket-account-id"
                v-model="settings.beepacket_account_id"
                type="text"
                :error="v$.beepacket_account_id.$error"
              />
            </div>
          </div>
        </div>

        <div v-show="currentTab === 'maps'" class="tab-pane-section">
          <div class="row">
            <div class="col-details">
              <label
                v-tooltip="'Se utiliza para normalizar direcciones. Recomendamos configurar tu propia region para obtener mejores resultados.'"
                for="map-locale"
                class="col-form-label"
              >
                zona
                <sup class="text-primary sup-question">?</sup>
              </label>
            </div>
            <div class="col">
              <argon-select
                id="map-locale"
                v-model="settings.maps_locale"
                :options="localeOptions"
                placeholder="Seleccione locale"
              />
            </div>
          </div>

          <div class="row">
            <div class="col-details">
              <label for="map-default-style" class="col-form-label">
                Estilo
              </label>
            </div>
            <div class="col">
              <argon-select
                id="map-default-style"
                v-model="settings.map_default_style"
                :options="MAP_STYLE_OPTIONS"
                placeholder="Seleccione estilo"
              />
            </div>
          </div>
        </div>

        <div v-show="currentTab === 'plans'" class="tab-pane-section plans-settings">
          <div class="row">
            <div class="col-details">
              <label for="plans-default-view" class="col-form-label">
                Vista
              </label>
            </div>
            <div class="col">
              <argon-select
                id="plans-default-view"
                v-model="settings.plans_default_view"
                :options="planViewOptions"
                placeholder="Seleccione vista"
              />
            </div>
          </div>

          <div class="row">
            <div class="col-details">
              <label
                v-tooltip="'Preselecciona este tipo al crear un plan nuevo. Si no lo indicas, se preseleccionará Recogida.'"
                for="plans-default-type"
                class="col-form-label"
              >
                Tipo de plan habitual
                <sup class="text-primary sup-question">?</sup>
              </label>
            </div>
            <div class="col">
              <argon-select
                id="plans-default-type"
                v-model="settings.default_plan_type"
                :options="PLAN_TYPE_OPTIONS"
                placeholder="Seleccione tipo"
              />
            </div>
          </div>
        </div>

        <div v-show="currentTab === 'mobile_app'" class="tab-pane-section mobile-app-settings">
          <div class="row">
            <div class="col-12">
              <argon-switch
                id="pickup-request-signature"
                v-model="settings.pickup_request_signature"
                name="pickup-request-signature"
              >
                Pedir firma en recogidas
              </argon-switch>
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <argon-switch
                id="delivery-request-signature"
                v-model="settings.delivery_request_signature"
                name="delivery-request-signature"
              >
                Pedir firma en entregas
              </argon-switch>
            </div>
          </div>

          <div class="row">
            <div class="col-details">
              <label for="pickup-finalize" class="col-form-label">
                Evidencias recogidas
              </label>
            </div>
            <div class="col">
              <argon-select
                id="pickup-finalize"
                v-model="settings.pickup_finalize"
                :options="mobileFinalizeOptions"
                placeholder="Seleccione opción"
              />
            </div>
          </div>

          <div class="row">
            <div class="col-details">
              <label for="delivery-finalize" class="col-form-label">
                Evidencias entregas
              </label>
            </div>
            <div class="col">
              <argon-select
                id="delivery-finalize"
                v-model="settings.delivery_finalize"
                :options="mobileFinalizeOptions"
                placeholder="Seleccione opción"
              />
            </div>
          </div>
        </div>

        <div v-show="currentTab === 'transportista'" class="tab-pane-section transportista-settings">
          <div class="row">
            <div class="col-details">
              <label
                v-tooltip="'Razón social, NIF y domicilio de tu empresa transportista. Se usan para generar el DeCA (Documento electrónico de Control Administrativo) cuando aplique.'"
                for="transportista-name"
                class="col-form-label required"
              >
                Razón social
                <sup class="text-primary sup-question">?</sup>
              </label>
            </div>
            <div class="col">
              <argon-input
                id="transportista-name"
                v-model="settings.name"
                type="text"
                :max-length="40"
                :error="v$.name.$error"
              />
            </div>
          </div>

          <div class="row">
            <div class="col-details">
              <label for="transportista-vat-number" class="col-form-label required">
                NIF
              </label>
            </div>
            <div class="col">
              <argon-input
                id="transportista-vat-number"
                v-model="settings.vat_number"
                type="text"
                :max-length="25"
                :error="v$.vat_number.$error"
              />
            </div>
          </div>

          <div class="row">
            <div class="col-details">
              <label for="transportista-address" class="col-form-label required">
                Dirección
              </label>
            </div>
            <div class="col">
              <argon-input
                id="transportista-address"
                v-model="settings.address"
                type="text"
                :max-length="120"
                :error="v$.address.$error"
              />
            </div>
          </div>
        </div>
      </form>
    </div>

    <div class="card-footer">
      <div class="float-end">
        <argon-loading-button
          class="btn btn-xs bg-gradient-primary m-0"
          color="primary"
          variant="gradient"
          :label="loading ? 'Guardando...' : 'Guardar'"
          :loading="loading"
          :disabled="fetching"
          @click.prevent="save"
        />
      </div>
    </div>
  </div>

  <div class="position-fixed top-1 end-1 z-index-2">
    <argon-snackbar
      v-if="snackbar"
      :title="snackbar.title"
      :description="snackbar.message"
      :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
      :color="snackbar.color"
      :close-handler="closeSnackbar"
    />
  </div>
</template>

<style scoped>
.col-details {
  flex: 0 0 auto;
  width: 100px !important;
}

.mobile-app-settings .col-details {
  width: 180px !important;
}

.plans-settings .col-details {
  width: 180px !important;
}

.transportista-settings .col-details {
  width: 120px !important;
}

.tab-pane-section {
  padding-top: 0.5rem;
}

.tab-pane-section .row + .row {
  margin-top: 0.75rem;
}
</style>
